import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Shield, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { CAMPUSES } from "@/lib/constants";
import { useContent } from "@/context/ContentContext";

const AUTH_IMG =
  "https://images.unsplash.com/photo-1507537362848-9c7e70b7b5c1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const { content } = useContent();
  const [form, setForm] = useState({ name: "", email: "", password: "", campus: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters.");
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created. Welcome to Patria ER CoE.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create account.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center p-6 sm:p-12 order-2 lg:order-1">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-charcoal-600 hover:text-forest-800 mb-8 lg:hidden">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Home
          </Link>
          <h1 className="font-serif text-3xl text-forest-900">Create your account</h1>
          <p className="mt-2 text-charcoal-600">Track the concerns you raise and receive updates.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="name" className="text-forest-900 font-semibold">Full name</Label>
              <Input id="name" data-testid="register-name-input" value={form.name} onChange={(e) => set("name", e.target.value)}
                className="mt-2 bg-bone rounded-lg border-forest-600/15 h-12 px-4" required />
            </div>
            <div>
              <Label htmlFor="email" className="text-forest-900 font-semibold">Email</Label>
              <Input id="email" type="email" data-testid="register-email-input" value={form.email} onChange={(e) => set("email", e.target.value)}
                className="mt-2 bg-bone rounded-lg border-forest-600/15 h-12 px-4" required />
            </div>
            <div>
              <Label htmlFor="password" className="text-forest-900 font-semibold">Password</Label>
              <Input id="password" type="password" data-testid="register-password-input" value={form.password} onChange={(e) => set("password", e.target.value)}
                placeholder="At least 6 characters" className="mt-2 bg-bone rounded-lg border-forest-600/15 h-12 px-4" required />
            </div>
            <div>
              <Label className="text-forest-900 font-semibold">Campus</Label>
              <Select value={form.campus} onValueChange={(v) => set("campus", v)}>
                <SelectTrigger data-testid="register-campus-select" className="mt-2 bg-bone rounded-lg border-forest-600/15 h-12 px-4">
                  <SelectValue placeholder="Select your campus" />
                </SelectTrigger>
                <SelectContent>
                  {(content.campuses || CAMPUSES).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={loading} data-testid="register-submit-btn"
              className="w-full rounded-full bg-terracotta-400 hover:bg-terracotta-500 text-white py-6 text-base">
              {loading ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Creating…</> : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-charcoal-600">
            Already have an account? <Link to="/login" className="font-semibold text-terracotta-500 hover:underline">Sign in</Link>
          </p>
        </motion.div>
      </div>

      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src={AUTH_IMG} alt="Supportive conversation" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-forest-900/60" />
        <div className="relative h-full flex flex-col justify-between p-12 text-bone">
          <div className="flex items-center gap-2.5 justify-end">
            <span className="font-serif text-lg font-semibold">Patria ER CoE</span>
            <span className="grid place-items-center h-9 w-9 rounded-xl bg-terracotta-400 text-white"><Shield className="h-5 w-5" strokeWidth={1.5} /></span>
          </div>
          <div className="text-right ml-auto max-w-md">
            <h2 className="font-serif text-4xl leading-tight">Your voice matters here.</h2>
            <p className="mt-4 text-bone/80">Raise a concern at any time — in addition to, or instead of, your supervisor.</p>
          </div>
          <span className="text-xs text-bone/50 text-right">Anonymous reporting always available.</span>
        </div>
      </div>
    </div>
  );
}
